import React, {cloneElement, useState, useContext, createContext} from 'react';
import PropTypes from 'prop-types';
import usePopper from './popper-hook';
import * as PLACEMENTS from '../../../constants/placements';
import ArrowContainer from './arrow';
import PopoverPanel from './panel';
import Arrow from './arrow.svg';
import {grid} from '../../../utils/grid';

export const PopperOffsetContext = createContext('0,0');

const HOVER_TRANSITION_TIMEOUT = 500;

const PopoverWithInteractive = ({
  anchor,
  placement,
  children,
  width = 'auto',
  padding = grid(2),
  hidden,
  customStyle,
  arrowColor,
  activateMode = 'manual',
  modifiersOffset = true,
  arrowStyle = {},
  flipPadding = {},
  delay
}) => {
  const {Manager, Reference, Popper} = usePopper();
  const [show, setShow] = useState(!hidden);
  const offset = useContext(PopperOffsetContext);

  let autoDismissTimer = null;

  const handleDismissByHover = () => {
    autoDismissTimer = setTimeout(() => setShow(false), delay ? delay : HOVER_TRANSITION_TIMEOUT);
  };

  const handleShow = () => setShow(!show);

  const cancelAutoDismiss = () => clearTimeout(autoDismissTimer);

  const refProp = ref =>
    anchor.type && anchor.type.isGlamorousComponent ? {innerRef: ref} : {ref};

  return Manager ? (
    <Manager>
      <Reference>
        {({ref}) =>
          cloneElement(anchor, {
            ...refProp(ref),
            onMouseEnter: () => handleShow(),
            onMouseLeave: () => handleDismissByHover()
          })
        }
      </Reference>
      {(activateMode === 'manual' ? !hidden : show) && (
        <Popper
          placement={placement}
          modifiers={{
            offset: {enabled: modifiersOffset, offset},
            preventOverflow: {enabled: true},
            flip: {
              enabled: true,
              padding: {...flipPadding},
              behavior: ['top', 'bottom']
            }
          }}
        >
          {({ref, style, placement, arrowProps}) => (
            <PopoverPanel
              width={width}
              padding={padding}
              innerRef={ref}
              style={{...style, ...customStyle}}
              data-placement={placement}
              onMouseEnter={() => cancelAutoDismiss()}
              onMouseLeave={() => handleDismissByHover()}
            >
              {children}
              <ArrowContainer
                innerRef={arrowProps.ref}
                data-placement={placement}
                style={{...arrowStyle, ...arrowProps.style}}
                arrowColor={arrowColor}
              >
                <Arrow />
              </ArrowContainer>
            </PopoverPanel>
          )}
        </Popper>
      )}
    </Manager>
  ) : (
    anchor
  );
};

PopoverWithInteractive.propTypes = {
  children: PropTypes.any,
  anchor: PropTypes.element,
  placement: PropTypes.oneOf([...Object.values(PLACEMENTS)]),
  width: PropTypes.number,
  padding: PropTypes.number,
  hidden: PropTypes.bool,
  customStyle: PropTypes.object,
  arrowColor: PropTypes.string,
  activateMode: PropTypes.oneOf(['manual', 'hover']),
  modifiersOffset: PropTypes.bool,
  arrowStyle: PropTypes.object,
  flipPadding: PropTypes.object,
  delay: PropTypes.number
};

export default PopoverWithInteractive;
